import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { PublicAssetShare } from 'fym-shared/src/music24/assetShares';
import { TypedMemo } from '../../common/types';
import { getFullAPIFileURL } from '../../common/getFullAPIFileURL';
import { publicShareKindOf } from './assetShareFacts';
import { shareArtColours, shareArtStyle } from './assetShareColours';
import { assetShareMessages } from './assetShareMessages';
import { AssetShareAmbient } from './AssetShareAmbient';
import type { OpenInRelease } from './AssetShareActions';
import { StoryBottom, StoryTop } from './AssetShareStoryParts';
import { AssetShareToast } from './AssetShareToast';
import { useShareLink } from './useShareLink';
import { useShareMediaLabel } from './useShareMediaLabel';
import { useStoryVideo } from './useStoryVideo';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the share page; each part uses its own slice of it */
import styles from './AssetShare.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * design: asset-share c6 (Theater, 9:16 reel full screen). A tall video fills the screen like a
 * Story; the title and the actions sit on a scrim on top of it. The sound starts off, and the sound
 * button turns it on. A tap on the video pauses it. The page is always dark: the video is the
 * ground. On a wide window the Story column is centered, and the blurred video lights the sides.
 */
const AssetShareStoryComponent: React.FC<{ share: PublicAssetShare; openInRelease: OpenInRelease | null }> = ({
  share,
  openInRelease,
}) => {
  const intl = useIntl();
  const artStyle = useMemo(() => shareArtStyle(shareArtColours(share.look, 'dark')), [share.look]);
  const { copied, shareLink } = useShareLink(share.song.title, publicShareKindOf(share.assetLabel));
  const video = useStoryVideo();
  const label = useShareMediaLabel(share);
  const progressStyle = useMemo(() => ({ width: `${Math.round(video.progress * 1000) / 10}%` }), [video.progress]);

  return (
    <div
      className={`${styles.page} ${styles.story}`}
      style={artStyle}
      data-testid="asset-share-public"
      data-layout="story"
    >
      <AssetShareAmbient share={share} className={styles.storyAmbient} />
      <div className={styles.storyStage}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a reel: its captions are burnt in */}
        <video
          ref={video.ref}
          className={styles.storyVideo}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={share.posterUrl !== null ? getFullAPIFileURL(share.posterUrl) : undefined}
          aria-label={label}
          onTimeUpdate={video.onTimeUpdate}
          onPlay={video.onPlay}
          onPause={video.onPause}
          data-testid="asset-share-video"
        >
          <source src={getFullAPIFileURL(share.mediaUrl)} type={share.contentType} />
          {intl.formatMessage(assetShareMessages.videoFallback)}
        </video>
        <button
          type="button"
          className={styles.storyTap}
          onClick={video.togglePlay}
          aria-label={intl.formatMessage(video.playing ? assetShareMessages.pause : assetShareMessages.play)}
        />
        <div className={styles.storyScrim} aria-hidden />
        <StoryTop share={share} muted={video.muted} onToggleMute={video.toggleMute} />
        <StoryBottom share={share} onShare={shareLink} openInRelease={openInRelease} />
        <span className={styles.storyProgress} aria-hidden>
          <span className={styles.storyProgressFill} style={progressStyle} />
        </span>
      </div>
      {copied && <AssetShareToast />}
    </div>
  );
};

export const AssetShareStory = TypedMemo(AssetShareStoryComponent);
